import appStore from '@/stores';
import AppController from "@/api/seller/Controller/AppController";
import { TrackEvent, EventListener } from './monitoringItem/baseListener';
import InputStayListener from './monitoringItem/inputStay';
import FieldErrorListener from './monitoringItem/fieldError';
import ButtonClickListener from './monitoringItem/buttonClick';
import ApiRequestListener from './monitoringItem/apiRequest';
import queryString from 'query-string';
import { getuuid } from '@/common/functions';
import { isEmpty, isEqual } from 'lodash-es';

export type ScheduledReportConfig = {
  interval: number; // 上报间隔(毫秒),建议最小值30000(30秒)
  enabledPages: string[]; // 启用定时上报的页面路径列表,支持通配符
}

export type TrackerConfig = {
  reportUrl?: string;
  reportNumberOnce?: number;
  listeners?: Array<any>;
  ext?: any;
  extUtils?: Array<any>;
  scheduledReportConfig?: ScheduledReportConfig; // 定时上报配置
}

export interface TrackerData {
  category: string, //分析的指标类型，例如：用户行为'userBehavior'
  traceId: string, //轨迹ID
  pathName: string, //页面路径
  ext?:any, //自定义扩展数据
  startTime:number,//开始时间戳
  endTime: number,//结束时间戳
  userId?: string,
  customerCode?: string,
  customerName?: string,
  href?: string,
}
type ReportParams = TrackerData & {
  nodes: TrackEvent[],
}

class Tracker {
  private container: HTMLElement;
  private otherConfig: any;
  private listeners: EventListener[] = [];
  private reportUrl: string;
  private reportNumberOnce: number;
  private flushIndex: number = 0;
  private failedReportedData: any[] = [];
  private eventQueue: TrackEvent[] = [];
  private scheduledReportTimer: NodeJS.Timeout | null = null; // 定时上报定时器
  private scheduledReportConfig?: ScheduledReportConfig; // 定时上报配置
  private trackerBaseData: TrackerData = {
    category:'',
    traceId: '',
    pathName: '',
    ext: {},
    startTime: 0,
    endTime: 0,
    userId: '',
    customerCode: '',
    customerName: '',
  };

  constructor(container: HTMLElement, config?: TrackerConfig) {
    this.container = container;
    this.otherConfig = config;
    this.reportUrl = config?.reportUrl ?? 'ulog.v1.add.uba';
    this.reportNumberOnce = config?.reportNumberOnce ?? 50;
    this.scheduledReportConfig = config?.scheduledReportConfig; // 保存定时上报配置
    this.createTrackerBaseData();
    this.initListeners();
    this.startScheduledReport(); // 启动定时上报
  }
  private createTrackerBaseData() {
    this.trackerBaseData = {
      category:'userBehavior',
      traceId: getuuid('userBehavior'),
      pathName: window.location.pathname,
      ext: {
        ...queryString.parse(window.location.search),
        ...(this.otherConfig?.ext || {})
      },
      startTime: 0,
      endTime: 0,
      userId: appStore.config?.userId,
      customerCode: appStore.config?.customerCode,
      customerName: appStore.config?.customerName,
      href: window.location.href,
    };
  }
  private initListeners() {
    this.listeners = [
      new InputStayListener(),
      new FieldErrorListener(),
      new ButtonClickListener(),
      new ApiRequestListener(),
      ...(this.otherConfig?.listeners??[]),
    ];
    const onEmit = this.handleEvent.bind(this);
    this.listeners.forEach(listener => listener.start(onEmit,this.container,this.trackerBaseData));
  }

  private handleEvent(event: TrackEvent, el?: HTMLElement) {
    event.ext = {
      lang: appStore.locale, //每条操作记录带上语言环境
      ...(event.ext || {}),
      ...(this.getModalName(el, event.nodeType) || {}),//弹窗内的节点操作记录弹窗名字
      ...(this.getSignKeyForParent(el) || {}),//记录操作所在行key或模块key
      ...(this.otherConfig?.extUtils || [])
        .reduce((exts: Record<string, any>, util: any) => ({ ...exts, ...util.getExt() }), {})
    };
    let lastEvent = this.eventQueue[this.eventQueue.length - 1];
    if ((event.nodeType === 'api' && event.url === this.reportUrl)
      || (event.nodeType === 'api' && !this.checkVisibility())
      || this.eventIsEqual(lastEvent,event)
    ) {
      return;
    }
    if (this.eventQueue.length === 0) {
      this.trackerBaseData.startTime = event.timestamp;
    }
    //接口记录和弹窗报错记录连续且间隔100ms内, 默认是接口报错，将报错信息合并到接口请求记录
    if (event.nodeType === 'errorModal'
      && lastEvent
      && lastEvent.nodeType === 'api'
      && event.timestamp - lastEvent.timestamp < 100) {
      let {nodeType, ...data} = event;
      lastEvent = {...lastEvent, ...data};
      this.eventQueue[this.eventQueue.length - 1] = lastEvent;
    } else {
      this.eventQueue.push(event);
    }
    // console.log('操作事件：',{
    //   ...this.trackerBaseData,
    //   endTime: Date.now(),
    //   nodes: this.eventQueue,
    // });

    this.reportingData();
  }
  //重新初始化
  public reset() {
    this.stopScheduledReport(); // 先停止定时器
    this.destroy();
    this.createTrackerBaseData();
    this.initListeners();
    this.startScheduledReport(); // 重新启动定时器
  }
  destroy() {
    this.stopScheduledReport(); // 停止定时器
    //文件下载window.location.href=xxx方式，会触发导致销毁监听
    // this.listeners.forEach(listener => listener.stop());
    // this.listeners = [];
    this.reportingDataLast();
  }
  //事件上报（达到阈值时触发）
  private reportingData() {
    const _eventQueue = this.eventQueue.slice(this.flushIndex);
    if (_eventQueue?.length < this.reportNumberOnce) {
      return;
    }
    this.flushEvents();
  }
  // 强制上报当前队列中未发送的事件
  private async flushEvents() {
    const _eventQueue = this.eventQueue.slice(this.flushIndex);
    if (_eventQueue.length === 0) return;
    this.flushIndex = this.eventQueue.length;
    const params = {
      ...this.trackerBaseData,
      endTime: Date.now(),
      nodes: _eventQueue,
    };
    console.log('[Monitor] 上报数据：', params, new Date().toLocaleString());
    const res = await AppController.ajaxProcess({
      api: this.reportUrl,
      form: params
    }, { throwError: false });
    if (res.status !== 1) {
      this.failedReportedData.push(params);
    }
  }
  public async reportingDataLast() {
    // 自动插入 LEAVE_PAGE 事件（仅在有事件记录时才插入）
    if (this.eventQueue.length > 0 || this.flushIndex > 0) {
      this.eventQueue.push({
        nodeType: 'custom',
        nodeDesc: 'LEAVE_PAGE',
        timestamp: Date.now(),
        ext: { action: 'LEAVE_PAGE', nodeIds: [] }
      } as TrackEvent);
    }
    let _failedReportedData = [...this.failedReportedData];
    if (_failedReportedData.length > 0) {
      this.failedReportedData = [];
      for (const params of _failedReportedData) {
        await this.sendBeaconData(params);
      }
    }
    const _eventQueue = this.eventQueue.slice(this.flushIndex);
    if (_eventQueue?.length) {
      let params:any ={
        ...this.trackerBaseData,
        endTime: Date.now(),
        nodes: _eventQueue,
      };
      this.sendBeaconData(params);
    }
  }
  private async sendBeaconData(params: ReportParams) {
    const formData = new FormData();
    if(isEmpty(params?.ext)){
      delete params.ext;
    }
    formData.append('api', this.reportUrl);
    formData.append('form', JSON.stringify(params));
    formData.append('jsondata', 'Y');
    // console.log('最后上报数据：', params);
    console.log('[Monitor] sendBeacon上报数据：', params, new Date().toLocaleString());
    navigator.sendBeacon(`${appStore.config.url.APP}/ajaxProcess`, formData);
    this.eventQueue = [];
    this.flushIndex = 0;
  }
  private getModalName(el?: HTMLElement, type?: string) {
    let modalName, modalKey, modalExt;
    let modalEl = el?.closest('.winitd-modal');
    if (type === 'api' && !el) {
      // 获取第一个显示的弹窗元素
      modalEl = Array.from(this.container?.querySelectorAll?.('.winitd-modal')).filter((ele) => {
        const htmlElement = ele as HTMLElement;
        const style = window.getComputedStyle(htmlElement);
        return style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              style.opacity !== '0' &&
              htmlElement.offsetWidth > 0 &&
              htmlElement.offsetHeight > 0;
      })?.[0] as HTMLElement;
    }
    if(!modalEl) return null;
    modalName = (modalEl?.querySelector('.winitd-modal-title') as HTMLElement)?.innerText?.trim();
    modalKey = modalEl.closest('.winitd-modal-root')?.getAttribute('data-modal-key')?.trim();
    try {
      modalExt = JSON.parse(modalEl.closest('.winitd-modal-root')?.getAttribute('data-monitor-ext') || '{}');
    } catch (e) {
      console.error(`解析自定义扩展数据失败:`, e);
    }
    if (!modalName && !modalKey) return null;
    return {
      modalName,
      modalKey,
      ...isEmpty(modalExt) ? {} : modalExt,
    };
  }
  private getSignKeyForParent(el?: HTMLElement) {
    let ext: any = {};
    //出库/入库/增值处理-增值服务的属性操作，需要记录属于那个增值服务
    if (el?.closest('tr') && el?.closest('tr')?.getAttribute('data-row-key')) {
      ext.rowKey = el?.closest('tr')?.getAttribute('data-row-key')?.toString?.();
    }
    if (el?.closest('.monitor-ext-box') && el?.closest('.monitor-ext-box')?.getAttribute('data-monitor-ext')) {
      try {
        let oext = JSON.parse(el?.closest('.monitor-ext-box')?.getAttribute('data-monitor-ext') || '{}');
        ext = { ...ext, ...(oext || {})};
      } catch (e) {
        console.error(`解析自定义扩展数据失败:`, e);
      }
    }
    return ext;
  }
  private checkVisibility() {
    if (typeof (this.container as any)?.checkVisibility === 'function') {
      return (this.container as any)?.checkVisibility();
    } else {
      return this.container?.offsetWidth > 0 && this.container?.offsetHeight > 0;
    }
  }
  //判断两个事件是否一样，超短时间内一样的事件去重
  private eventIsEqual(prevEvent:any, curEvent:any) {
    if (!prevEvent || !curEvent) return false;
    let { timestamp:prevt, ...preve } = prevEvent;
    let { timestamp:curt, ...cure } = curEvent;
    return Number(curt)-Number(prevt)<10 && isEqual(preve, cure);
  }

  /**
   * 判断当前页面是否启用定时上报
   * @param pathname 页面路径
   * @returns 是否启用
   */
  private isPageEnabled(pathname: string): boolean {
    if (!this.scheduledReportConfig?.enabledPages || this.scheduledReportConfig.enabledPages.length === 0) {
      return false;
    }

    return this.scheduledReportConfig.enabledPages.some(pattern => {
      // 精确匹配
      if (pattern === pathname) {
        return true;
      }

      // 通配符匹配: /seller/* 匹配 /seller/xxx
      if (pattern.includes('*')) {
        try {
          const regexPattern = pattern.replace(/\*/g, '.*');
          const regex = new RegExp(`^${regexPattern}$`);
          return regex.test(pathname);
        } catch (e) {
          console.error(`定时上报路径匹配失败: ${pattern}`, e);
          return false;
        }
      }

      return false;
    });
  }

  /**
   * 启动定时上报
   */
  private startScheduledReport(): void {
    // 未配置或配置无效
    if (!this.scheduledReportConfig) {
      return;
    }

    const { interval, enabledPages } = this.scheduledReportConfig;

    // 验证配置
    if (!enabledPages || enabledPages.length === 0) {
      console.info('[Monitor] 定时上报未启用: enabledPages 为空');
      return;
    }

    if (!interval || interval < 30000) {
      console.warn(`[Monitor] 定时上报间隔过小 (${interval}ms), 使用默认值 300000ms (5分钟)`);
      this.scheduledReportConfig.interval = 300000;
    }

    // 检查当前页面是否匹配
    const currentPath = window.location.pathname;
    if (!this.isPageEnabled(currentPath)) {
      console.info(`[Monitor] 当前页面 ${currentPath} 未启用定时上报`);
      return;
    }

    // 启动定时器
    try {
      this.scheduledReportTimer = setInterval(() => {
        this.handleScheduledReport();
      }, this.scheduledReportConfig.interval);
      console.log(`[Monitor] 定时上报已启动, 间隔: ${this.scheduledReportConfig.interval}ms, 页面: ${currentPath}`);
    } catch (e) {
      console.error('[Monitor] 定时上报启动失败:', e);
      this.scheduledReportTimer = null;
    }
  }

  /**
   * 停止定时上报
   */
  private stopScheduledReport(): void {
    if (this.scheduledReportTimer) {
      clearInterval(this.scheduledReportTimer);
      this.scheduledReportTimer = null;
      console.log('[Monitor] 定时上报已停止');
    }
  }

  /**
   * 定时上报处理逻辑
   */
  private handleScheduledReport(): void {
    const _eventQueue = this.eventQueue.slice(this.flushIndex);
    if (_eventQueue.length === 0) {
      console.log('[Monitor] 定时上报触发, 但事件队列为空, 跳过');
      return;
    }
    // 如果队列中只有 ENTER_PAGE 一条事件，不上报（等有实际操作再上报）
    if (_eventQueue.length === 1 && _eventQueue[0].nodeDesc === 'ENTER_PAGE') {
      console.log('[Monitor] 定时上报触发, 仅有 ENTER_PAGE, 跳过');
      return;
    }
    console.log(`[Monitor] 定时上报触发, 上报 ${_eventQueue.length} 条事件`, new Date().toLocaleString());
    this.flushEvents();
  }

  /**
   * 手动上报自定义事件（用于非 DOM 交互触发的业务事件）
   */
  public trackEvent(event: Partial<TrackEvent>) {
    this.handleEvent({
      nodeType: event.nodeType || 'custom',
      timestamp: Date.now(),
      ...event,
    } as TrackEvent);
  }

  /**
   * 手动上报自定义事件并立即 flush（用于页面离开等场景）
   */
  public trackEventImmediate(event: Partial<TrackEvent>) {
    this.trackEvent(event);
    this.reportingDataLast();
  }
}
export default Tracker;
